import { parseDateOnly, type Draw } from '@/lib/drawCore';

// ── 통계 파생 계산 (순수 함수) ─────────────────────────────────────────────
// 데이터 처리 순서: 전체 원본 → 기간 필터 → 고급 필터 → 빈도/pair/triple/요약

export interface AdvancedFilters {
  numberRanges: number[];
  dayOfWeeks: number[];
  prizeRange: string;
}

export const EMPTY_ADVANCED_FILTERS: AdvancedFilters = {
  numberRanges: [],
  dayOfWeeks: [],
  prizeRange: '',
};

export interface StatsSummary {
  totalDraws: number;
  latestDraw: number;
  hottestNumber: number;
  hottestCount: number;
  coldestNumber: number;
  coldestCount: number;
  averagePerNumber: number;
}

/**
 * YYYY-MM-DD 요일 판정 — 브라우저 타임존에 따라 달라지지 않도록
 * 날짜 문자열을 직접 파싱해 UTC 기준 weekday를 계산한다.
 * 비정상/무효 날짜는 Date 자동 보정이나 로컬 타임존 fallback 없이 명시적으로 -1(무효)을 반환한다.
 * (요일 필터 값은 0~6이므로 -1은 어떤 요일에도 매칭되지 않아 자연스럽게 제외됨)
 */
export function getDayOfWeek(dateStr: string): number {
  const parsed = parseDateOnly(dateStr);
  if (!parsed) return -1;
  return new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day)).getUTCDay();
}

/**
 * 1등 당첨금 구간 분류.
 * - null/undefined/비정상 → '' (어떤 필터에도 매칭되지 않아 "1억 미만"으로 오분류하지 않음)
 * - 경계는 반개구간 [하한, 상한)으로 5억/10억/30억이 중복·누락되지 않게 처리
 *   under1eok:   < 1억
 *   1to5eok:     [1억, 5억)
 *   5to10eok:    [5억, 10억)
 *   10to30eok:   [10억, 30억)
 *   over30eok:   >= 30억
 */
export function parsePrizeRange(prize: number | null | undefined): string {
  if (prize == null || typeof prize !== 'number' || !Number.isFinite(prize) || prize < 0) {
    return '';
  }
  if (prize < 100_000_000) return 'under1eok';
  if (prize < 500_000_000) return '1to5eok';
  if (prize < 1_000_000_000) return '5to10eok';
  if (prize < 3_000_000_000) return '10to30eok';
  return 'over30eok';
}

export function hasAdvancedFilters(filters: AdvancedFilters | null | undefined): boolean {
  if (!filters) return false;
  return (
    filters.numberRanges.length > 0 ||
    filters.dayOfWeeks.length > 0 ||
    filters.prizeRange !== ''
  );
}

/** 기간 필터: 최근 N회차 (draws는 최신순 내림차순 정렬 전제) */
export function filterByPeriod(draws: Draw[], limit?: number | null): Draw[] {
  if (!limit || limit <= 0) return draws;
  return draws.slice(0, limit);
}

const RANGE_BOUNDS: Record<number, { min: number; max: number }> = {
  1: { min: 1, max: 10 },
  2: { min: 11, max: 20 },
  3: { min: 21, max: 30 },
  4: { min: 31, max: 40 },
  5: { min: 41, max: 45 },
};

/** 고급 필터: 번호대 / 요일 / 당첨금 구간 (이미 받은 원본 데이터에만 적용) */
export function applyAdvancedFilters(
  draws: Draw[],
  filters: AdvancedFilters | null | undefined,
): Draw[] {
  if (!filters || !hasAdvancedFilters(filters)) return draws;
  let result = draws;

  if (filters.numberRanges.length > 0) {
    result = result.filter((draw) =>
      filters.numberRanges.some((r) => {
        const bounds = RANGE_BOUNDS[r];
        if (!bounds) return false;
        return draw.numbers.some((n) => n >= bounds.min && n <= bounds.max);
      }),
    );
  }

  if (filters.dayOfWeeks.length > 0) {
    result = result.filter((draw) =>
      filters.dayOfWeeks.includes(getDayOfWeek(draw.draw_date)),
    );
  }

  if (filters.prizeRange) {
    result = result.filter(
      (draw) => parsePrizeRange(draw.prize_1st) === filters.prizeRange,
    );
  }

  return result;
}

export function computeFrequency(draws: Draw[]): Map<number, number> {
  const freq = new Map<number, number>();
  for (let i = 1; i <= 45; i++) freq.set(i, 0);
  for (const draw of draws) {
    for (const num of draw.numbers) {
      freq.set(num, (freq.get(num) ?? 0) + 1);
    }
  }
  return freq;
}

export function getHotNumbers(freq: Map<number, number>, count = 6): number[] {
  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, count)
    .map(([n]) => n);
}

export function getColdNumbers(freq: Map<number, number>, count = 6): number[] {
  return Array.from(freq.entries())
    .sort((a, b) => a[1] - b[1])
    .slice(0, count)
    .map(([n]) => n);
}

export function getHotCombination(freq: Map<number, number>): { numbers: number[]; bonus: number } {
  const sorted = Array.from(freq.entries()).sort((a, b) => b[1] - a[1]);
  const numbers = sorted.slice(0, 6).map(([n]) => n).sort((a, b) => a - b);
  const bonus = sorted[6]?.[0] ?? 45;
  return { numbers, bonus };
}

export function getColdCombination(freq: Map<number, number>): { numbers: number[]; bonus: number } {
  const sorted = Array.from(freq.entries()).sort((a, b) => a[1] - b[1]);
  const numbers = sorted.slice(0, 6).map(([n]) => n).sort((a, b) => a - b);
  const bonus = sorted[6]?.[0] ?? 45;
  return { numbers, bonus };
}

export function computeStatsSummary(
  draws: Draw[],
  freq: Map<number, number>,
  hot: number[],
  cold: number[],
): StatsSummary | null {
  if (draws.length === 0) return null;
  return {
    totalDraws: draws.length,
    latestDraw: draws[0]?.id ?? 0,
    hottestNumber: hot[0] ?? 0,
    hottestCount: freq.get(hot[0] ?? 0) ?? 0,
    coldestNumber: cold[0] ?? 0,
    coldestCount: freq.get(cold[0] ?? 0) ?? 0,
    averagePerNumber: Math.round((draws.length * 6) / 45),
  };
}